import { ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { BaseDashboardFiltersComponent } from '../base-dashboard-filters.component';
import { GaValidateService } from '../../../../../shared/formly/ga-validate.service';
import { GaMessageService } from '../../../../../shared/message/ga-message.service';
import { TranslationService } from '../../../../../shared/translation/translation.service';
import { ReportDataService } from '../../../../../shared/report/report-data.service';
import { ReportFiltersUtil } from '../../../../../shared/report/report-filters-util.service';
import { DashboardService } from '../../dashboard.service';
import { Override } from '../../../../../shared/app-decorators';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../../../shared/formly/ga-formly.service';
import { GaDeferredService } from '../../../../../shared/services/ga-deferred.service';
import { ICompanyCountryExtended, IContact, ICurrency } from '../../../../../../type_definitions/datatypes';
import { FormattingService } from '../../../../../shared/services/formatting.service';
import * as _ from 'underscore';
import { AuthService } from '../../../../../shared/services/auth/auth.service';
import { DashboardDataService } from '../../../../../shared/report/dashboard-data.service';
import { OdDataService } from './od-data.service';
import { WebshopDataService } from '../../../../webshop/webshop-data.service';
import { CompanyDataService } from '../../../../../shared/company/company-data.service';
import { ContactDataService } from '../../../../../shared/contact/contact-data.service';
import { GaEventService } from '../../../../../shared/services/ga-event.service';
import { RoleContactType } from '../../../../../shared/contact/role-contact-type';

@Component({
  selector: 'od-dashboard-filters',
  templateUrl: '../base-dashboard-filters.component.html'
})
export class OdDashboardFiltersComponent extends BaseDashboardFiltersComponent implements OnDestroy {
  @Input() public showCustomerFilter:boolean;
  @Input() public showNAMFilter:boolean;
  @Input() public showDIYFilter:boolean;

  private pssField:IExtraFieldConfigurationObject;
  private currencyField:IExtraFieldConfigurationObject;
  private bpField:IExtraFieldConfigurationObject;
  private vatField:IExtraFieldConfigurationObject;
  private bpGroupField:IExtraFieldConfigurationObject;
  private customerField:IExtraFieldConfigurationObject;
  private companyGroupField:IExtraFieldConfigurationObject;
  private customerCompanyField:IExtraFieldConfigurationObject;
  private namField:IExtraFieldConfigurationObject;
  private diyField:IExtraFieldConfigurationObject;
  private bpDeskField:IExtraFieldConfigurationObject;

  constructor(
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private dashboardDataService:DashboardDataService,
    private odDataService:OdDataService,
    private webshopDataService:WebshopDataService,
    private companyDataService:CompanyDataService,
    private contactDataService:ContactDataService,
    validateService:GaValidateService,
    messageService:GaMessageService,
    translationService:TranslationService,
    reportDataService:ReportDataService,
    reportFiltersUtil:ReportFiltersUtil,
    dashboardService:DashboardService,
    deferredService:GaDeferredService,
    authService:AuthService,
    cdr:ChangeDetectorRef,
    eventService:GaEventService
  ) {
    super(validateService, messageService, translationService, reportDataService, reportFiltersUtil, dashboardService, deferredService, authService, cdr, eventService);
  }

  protected customValidatorFn = ():Promise<void | string[]> => {
    if (!this.model.vatIncl) {
      return Promise.resolve();
    }

    if (this.isACSLUser && !this.model.pss && !this.model.bp && !this.model.bpGroup) {
      return Promise.reject('Please select at least an PSS or a BP.');
    } else if (!this.isACSLUser && !this.model.bp && !this.model.bpGroup) {
      return Promise.reject('Please select a BP.');
    }

    return Promise.resolve();
  };

  @Override(BaseDashboardFiltersComponent)
  public fillFields():void {
    const promises:Promise<any>[] = [];

    // don't load ODs for other users, because they don't have access
    if (this.isACSLUser) {
      promises.push(
        this.formlyService.fillField(this.pssField, this.dashboardDataService.ODs())
          .then(() => {
            // preselect current user
            this.form.patchValue({ pss: _.findWhere(this.pssField.templateOptions.options, { id: this.filters.pss || this.currentUser.id }) });
          })
      );
    }

    promises.push(
      this.formlyService.fillField(this.currencyField, this.dashboardDataService.currencies())
        .then((currencies:ICurrency[]) => {
          this.form.patchValue({ currency: _.findWhere(currencies, { id: this.filters.currency }) });
        })
    );

    promises.push(
      this.formlyService.fillField(this.vatField, this.dashboardDataService.yesNo())
        .then((yesNo) => {
          this.form.patchValue({ vatIncl: <boolean>_.findWhere(yesNo, { id: this.filters.vatIncl }) });
        })
    );

    promises.push(
      this.formlyService.fillField(this.bpField, this.dashboardDataService.BPs())
        .then(() => this.preselectBP())
    );
    promises.push(
      this.formlyService.fillField(this.bpGroupField, this.odDataService.BPGroups())
    );

    if (this.customerField) {
      promises.push(
        this.formlyService.fillField(this.customerField, this.webshopDataService.webshops())
      );
    }
    if (this.customerCompanyField) {
      promises.push(
        this.formlyService.fillField(this.customerCompanyField, this.companyDataService.customerCompanies())
      );
    }
    if (this.companyGroupField) {
      promises.push(
        this.formlyService.fillField(this.companyGroupField, this.dashboardDataService.companyGroups())
      );
    }

    if (this.namField) {
      promises.push(
        this.formlyService.fillField(this.namField, this.contactDataService.NAMs(RoleContactType.NATIONAL_ACCOUNT_MANAGER))
      );
    }

    if (this.diyField) {
      promises.push(
        this.formlyService.fillField(this.diyField, this.dashboardDataService.yesNo())
      );
    }

    promises.push(
      this.formlyService.fillField(this.bpDeskField, this.dashboardDataService.odTeams())
    );

    // fulfilling all promises then set query filters or autoSearch after
    Promise.all(promises)
      .then(this.setQueryFilters)
      .then(() => {
        if (this.autoSearch) {
          this.startSearch(true);
        }
      });
  }

  @Override(BaseDashboardFiltersComponent)
  public createFields():IExtraFieldConfigurationObject[] {
    return [
      this.pssField = this.formlyService.aSelect('pss', 'PSS')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatContact)
        .withHideExpression(() => !this.isACSLUser)
        .build(),

      this.currencyField = this.formlyService.aSelect('currency', 'Currency')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatCurrencyL)
        .build(),

      this.bpField = this.formlyService.aSearch('bp', 'BP')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatBPNicknameCountry)
        .onChange((bp:ICompanyCountryExtended) => {
          // clear NAM field when BP company country is different than company country of selected NAM
          if (bp && bp.companyCountryId !== this.model.nam?.companyCountryId) {
            this.form.patchValue({ nam: undefined });
          }
        })
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .build(),

      this.vatField = this.formlyService.aSelect('vatIncl', 'VAT incl')
        .isRequired()
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatLov)
        .build(),

      this.bpGroupField = this.formlyService.aSearch('bpGroup', 'BP group')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatName)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .build(),

      this.customerCompanyField = this.formlyService.aSearch('customerCompany', 'Customer company')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatCompany)
        .withHideExpression(() => !this.showCustomerFilter)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .build(),

      this.companyGroupField = this.formlyService
        .aSearch('companyGroup', 'Company group')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatCompany)
        .withHideExpression(() => !this.showCustomerFilter)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .build(),

      this.customerField = this.formlyService.aSearch('customer', 'Customer')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatCompany)
        .withHideExpression(() => !this.showCustomerFilter)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .build(),

      this.namField = this.formlyService.aSearch('nam', 'NAM')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatContact)
        .onChange((nam:IContact) => {
          if (nam) {
            this.preselectBP(nam.companyCountryId);
          }
        })
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .withHideExpression(() => !this.showNAMFilter)
        .build(),

      this.bpDeskField = this.formlyService.aMultiSelect('bpDesk', 'BP Desk')
        .withWrapperClass(this.wrapperClass)
        .withValueProp('id')
        .withLabelProp('description')
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .build(),

      this.diyField = this.formlyService.aSelect('diy', 'Do it yourself (DIY)')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatLov)
        .withHideExpression(() => !this.showDIYFilter)
        .build()
    ];
  }

  private preselectBP(companyCountryId?:number):void {
    const selectBP = (companyCountryId:number):number => {
      const bp:ICompanyCountryExtended = _.findWhere(this.bpField.templateOptions.options, { companyCountryId });
      if (bp) {
        this.form.patchValue({ bp });
      }
      return bp?.id;
    };

    if (companyCountryId) {
      selectBP(companyCountryId);
    } else {
      const ccId:number = this.companyCountryId? parseInt(this.companyCountryId) : this.currentUser.location?.companyCountry?.id;
      selectBP(ccId);
    }
  }

  public ngOnDestroy():void {
    this.odDataService.clear();
    this.dashboardDataService.clear();
  }
}